/**
 * The lifted plan itself (see lift.ts): pure, and made on the main thread or in the lift worker alike.
 */

/** Paper white is lifted to this (out of 255): a whisper under white, so a lifted stretch still reads as the plan. */
const TARGET = 246;
/** Tone at or below this is ink, and not lifted; above `SHADE` it is shading, lifted in full; between, eased. */
const INK = 64,
  SHADE = 104;

function canvasOf(w: number, h: number): HTMLCanvasElement | OffscreenCanvas {
  if (typeof document !== 'undefined') {
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    return c;
  }
  return new OffscreenCanvas(w, h);
}

export function makeLifted(image: CanvasImageSource, w: number, h: number): HTMLCanvasElement | OffscreenCanvas | null {
  const k = Math.min(1, 4096 / Math.max(w, h));
  const cw = Math.max(1, Math.round(w * k)),
    ch = Math.max(1, Math.round(h * k));
  const canvas = canvasOf(cw, ch);
  const ctx = canvas.getContext('2d', { willReadFrequently: true }) as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null;
  if (!ctx) return null;
  ctx.drawImage(image, 0, 0, cw, ch);
  const img = ctx.getImageData(0, 0, cw, ch);
  const px = img.data;
  const n = cw * ch;
  const lum = new Uint8Array(n);
  for (let i = 0; i < n; i++) lum[i] = (54 * px[4 * i]! + 183 * px[4 * i + 1]! + 19 * px[4 * i + 2]!) >> 8;
  // the window: wider than any line or letter (≈ 4 units of the markup, u = longest side / 1000)
  const r = Math.max(2, Math.round((4 * Math.max(cw, ch)) / 1000));
  const tone = closing(lum, cw, ch, r);
  // a plan that is all paper and ink has nothing to lift: marks are drawn on it as they always were
  let shaded = 0;
  for (let i = 0; i < n; i++) if (tone[i]! < 236 && tone[i]! > INK) shaded++;
  if (shaded < 0.004 * n) return null;
  // the lift for each tone, once
  const lift = new Float32Array(256);
  for (let b = 0; b < 256; b++) {
    if (b >= TARGET) lift[b] = 1;
    else if (b <= INK) lift[b] = 1;
    else {
      const full = TARGET / Math.max(b, SHADE);
      const t = Math.min(1, (b - INK) / (SHADE - INK));
      lift[b] = 1 + (full - 1) * t * t * (3 - 2 * t);
    }
  }
  for (let i = 0; i < n; i++) {
    const f = lift[tone[i]!]!;
    if (f === 1) continue;
    const j = 4 * i;
    px[j] = Math.min(255, px[j]! * f);
    px[j + 1] = Math.min(255, px[j + 1]! * f);
    px[j + 2] = Math.min(255, px[j + 2]! * f);
  }
  ctx.putImageData(img, 0, 0);
  return canvas;
}

/** A morphological closing of a lightness map: the largest value within `r` each way, then the smallest (square window, separable). */
export function closing(src: Uint8Array, w: number, h: number, r: number): Uint8Array {
  // rows, then columns (on the transposed map, so every pass reads in order)
  const a = new Uint8Array(src.length);
  rowRuns(src, a, w, h, r, true);
  const t = transpose(a, w, h);
  const b = new Uint8Array(src.length);
  rowRuns(t, b, h, w, r, true);
  rowRuns(b, t, h, w, r, false);
  const c = transpose(t, h, w);
  rowRuns(c, a, w, h, r, false);
  return a;
}

function transpose(src: Uint8Array, w: number, h: number): Uint8Array {
  const out = new Uint8Array(src.length);
  const B = 32;
  for (let y0 = 0; y0 < h; y0 += B)
    for (let x0 = 0; x0 < w; x0 += B) {
      const y1 = Math.min(h, y0 + B),
        x1 = Math.min(w, x0 + B);
      for (let y = y0; y < y1; y++) {
        const o = y * w;
        for (let x = x0; x < x1; x++) out[x * h + y] = src[o + x]!;
      }
    }
  return out;
}

/**
 * The running maximum (or minimum) over a window of 2r+1 along every row, in linear time (van Herk
 * / Gil–Werman: a prefix and a suffix run per block of the window's width); beyond the row's ends
 * the values do not count.
 */
function rowRuns(src: Uint8Array, dst: Uint8Array, w: number, h: number, r: number, max: boolean): void {
  const win = 2 * r + 1;
  const total = w + 2 * r;
  const line = new Uint8Array(total),
    pre = new Uint8Array(total),
    suf = new Uint8Array(total);
  const pad = max ? 0 : 255;
  for (let y = 0; y < h; y++) {
    const o = y * w;
    line.fill(pad, 0, r);
    line.set(src.subarray(o, o + w), r);
    line.fill(pad, r + w, total);
    // a run forward and a run back within each block of the window's width
    for (let b = 0; b < total; b += win) {
      const e = Math.min(total, b + win);
      let m = line[b]!;
      pre[b] = m;
      if (max) for (let i = b + 1; i < e; i++) pre[i] = m = line[i]! > m ? line[i]! : m;
      else for (let i = b + 1; i < e; i++) pre[i] = m = line[i]! < m ? line[i]! : m;
      m = line[e - 1]!;
      suf[e - 1] = m;
      if (max) for (let i = e - 2; i >= b; i--) suf[i] = m = line[i]! > m ? line[i]! : m;
      else for (let i = e - 2; i >= b; i--) suf[i] = m = line[i]! < m ? line[i]! : m;
    }
    if (max)
      for (let i = 0; i < w; i++) {
        const a = suf[i]!,
          c = pre[i + win - 1]!;
        dst[o + i] = a > c ? a : c;
      }
    else
      for (let i = 0; i < w; i++) {
        const a = suf[i]!,
          c = pre[i + win - 1]!;
        dst[o + i] = a < c ? a : c;
      }
  }
}
